// V1-COMPAT: the React 18 mount bridge for the component trees copied from V2.
//
// V1 has one global mount bridge (packs/frontend_javascript.js): react_ujs finds every
// `data-react-class` element and renders it with React 16's ReactDOM.render. The copied V2 trees are
// written against React 18 and resolve their `react` import to the second, aliased React install
// (see config/webpack/base.js), so rendering them through that bridge would put React 18 components
// inside a React 16 tree. They need their own roots.
//
// So this module is a second, parallel bridge with its own marker attributes. The Rails side emits
// them with the `react18_component` helper (components/frontend/app/helpers/frontend/react18_helper.rb),
// which deliberately does NOT emit `data-react-class`, so react_ujs never sees these elements and the
// two bridges cannot fight over the same DOM node.
//
// The registry below is written out by hand. react_ujs discovers components with a webpack
// `require.context` sweep of the whole react_components directory; here every mountable name is
// listed explicitly, so nothing becomes mountable under React 18 just by existing in the tree.
// Names match the ones V2 mounts for the same pages, so an ERB view moved between the two
// applications keeps working.
import React from 'react';
import { createRoot } from 'react-dom/client';

import vlog from '../../vlog';

import SourceDirectory from '../../../react_components/source_directory/SourceDirectory';
import V1SourceDirectory from '../../../react_components/v2_host_glue/source_directory_host';
import V1ExpertShow from '../../../react_components/v2_host_glue/expert_show_host';
import V1CompanyShow from '../../../react_components/v2_host_glue/company_show_host';
import V1ProductShow from '../../../react_components/v2_host_glue/product_show_host';
import V1PrProfileShow from '../../../react_components/v2_host_glue/pr_profile_show_host';
import V1MediaProfileShow from '../../../react_components/v2_host_glue/media_profile_show_host';
import V1MediaOutletShow from '../../../react_components/v2_host_glue/media_outlet_show_host';
import V1MediaOutletsIndex from '../../../react_components/v2_host_glue/media_outlets_index_host';
import ExpertTopLevelShow from '../../../react_components/experts/expert_top_level_show';
import CompanyTopLevelShow from '../../../react_components/companies/company_top_level_show';
import ProductTopLevelShow from '../../../react_components/products/product_top_level_show';
import PrProfileTopLevelShow from '../../../react_components/pr_profiles/pr_profile_top_level_show';
import MediaProfileTopLevelShow from '../../../react_components/media_profiles/media_profile_top_level_show';
import MediaOutletTopLevelShow from '../../../react_components/media_outlets/media_outlet_top_level_show';
import AvatarStack from '../../../react_components/feeds/AvatarStack';
import HeaderKebabMenu from '../../../react_components/profiles/HeaderKebabMenu/HeaderKebabMenu';

const CLASS_ATTR = 'data-react18-class';
const PROPS_ATTR = 'data-react18-props';

const COMPONENTS = {
  CompanyTopLevelShow,
  ExpertTopLevelShow,
  MediaOutletTopLevelShow,
  MediaProfileTopLevelShow,
  PrProfileTopLevelShow,
  ProductTopLevelShow,
  SourceDirectory,
  // V1-COMPAT: the V1-hosted source directory -- SourceDirectory wrapped with the V2 identity
  // glue (see react_components/v2_host_glue/), mounted by the five directory index actions
  // (sources/products/pr_users/companies/story_ideas) once resolve_render_version picks :v2.
  V1SourceDirectory,

  // V1-COMPAT: the V1-hosted profile SHOW pages -- the bare *TopLevelShow components above,
  // each wrapped with the entity-resolution + identity glue (see with_v2_entity.jsx and
  // with_v2_identity.jsx via v2_host_glue/*_show_host.jsx) so they can be mounted with only the
  // V1 record id V1 actually has. Mounted by
  // sources/companies/products/pr_users/reporters/publications#show once
  // v2_profile_show_render_version picks :v2.
  V1ExpertShow,
  V1CompanyShow,
  V1ProductShow,
  V1PrProfileShow,
  V1MediaProfileShow,
  V1MediaOutletShow,

  // V1-COMPAT: V2's media outlets index body on V1's /publications page. Unlike V1SourceDirectory
  // this mounts one search panel WITHOUT V2's heading and tab bar -- V1's own Media Database
  // header supplies those. Mounted by publications#index once the render version picks :v2.
  V1MediaOutletsIndex,

  // Neither of these is a page. They are the two cheapest copied components to render in a real
  // browser, which makes them the standing check that this bridge and the React 18 resolution behind
  // it still work. AvatarStack needs nothing but plain props and covers the first-party path.
  // HeaderKebabMenu covers the harder one: it renders a react-bootstrap Dropdown, whose hooks live in
  // @restart/ui and @restart/hooks, so it only works if those shared packages resolved to React 18
  // too. Both stay registered because that check is worth keeping.
  AvatarStack,
  HeaderKebabMenu,
};

// Element -> React root. Roots have to be kept because React 18 unmounts through the root object,
// not through the container element, and Turbolinks throws the container away on every navigation.
const mountedRoots = new Map();

function readProps(element) {
  const raw = element.getAttribute(PROPS_ATTR);
  if (!raw) return {};

  try {
    return JSON.parse(raw);
  } catch (error) {
    vlog.error(`[react18] ${PROPS_ATTR} on this element is not valid JSON`, element);
    throw error;
  }
}

function mountComponents() {
  document.querySelectorAll(`[${CLASS_ATTR}]`).forEach((element) => {
    if (mountedRoots.has(element)) return;

    const name = element.getAttribute(CLASS_ATTR);
    const Component = COMPONENTS[name];

    if (!Component) {
      vlog.error(`[react18] cannot find component '${name}' for element`, element);
      throw new Error(`Cannot find React 18 component: '${name}'. Add it to react18_mount.js.`);
    }

    const root = createRoot(element);
    mountedRoots.set(element, root);
    root.render(React.createElement(Component, readProps(element)));
  });
}

function unmountComponents() {
  mountedRoots.forEach((root) => root.unmount());
  mountedRoots.clear();
}

// Turbolinks 5 replaces the body on every visit and caches a snapshot of the outgoing page. Roots
// have to be torn down before either happens, or React keeps rendering into detached elements and
// the cached snapshot holds a copy of React-rendered DOM that the next mount would render over.
// `turbolinks:load` is the same event V1's react_ujs bridge mounts on, and it fires on first load
// too; the readyState branch only matters for a page that has no Turbolinks running.
document.addEventListener('turbolinks:load', mountComponents);
document.addEventListener('turbolinks:before-cache', unmountComponents);
document.addEventListener('turbolinks:before-render', unmountComponents);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountComponents);
} else {
  mountComponents();
}
